import type { client } from "@duress/shared";

export type HealthBand = "excellent" | "healthy" | "needs_attention" | "at_risk";

export function bandFor(score: number): HealthBand {
  if (score >= 80) return "excellent";
  if (score >= 60) return "healthy";
  if (score >= 40) return "needs_attention";
  return "at_risk";
}

export const BAND_META: Record<
  HealthBand,
  { label: string; dotClass: string; textClass: string; bgClass: string }
> = {
  excellent: {
    label: "Excellent",
    dotClass: "bg-falcon-green",
    // text-falcon-green is ~2.2:1 on a white card — fails AA. text-pill-green
    // is a darkened/brightened-per-theme variant tuned for text contrast.
    textClass: "text-pill-green",
    bgClass: "bg-falcon-green/10",
  },
  healthy: {
    label: "Healthy",
    dotClass: "bg-eagle-blue",
    // text-eagle-blue is ~3.0:1 on the dark card — fails AA there.
    textClass: "text-pill-blue",
    bgClass: "bg-eagle-blue/10",
  },
  needs_attention: {
    label: "Needs attention",
    dotClass: "bg-phoenix-orange",
    // text-phoenix-orange is ~2.4:1 on a white card — fails AA.
    textClass: "text-pill-orange",
    bgClass: "bg-phoenix-orange/10",
  },
  at_risk: {
    label: "At risk",
    dotClass: "bg-emergency-red",
    // text-emergency-red is ~3.0:1 on the dark card — fails AA there.
    textClass: "text-pill-red",
    bgClass: "bg-emergency-red/10",
  },
};

export interface PortfolioSummary {
  total: number;
  scored: number;
  excellent: number;
  healthy: number;
  needsAttention: number;
  atRisk: number;
  averageScore: number | null;
}

export function summarisePortfolio(clients: client.ClientListItem[]): PortfolioSummary {
  const scored = clients.filter(
    (c): c is client.ClientListItem & { healthScore: number } => c.healthScore !== null,
  );
  let excellent = 0;
  let healthy = 0;
  let needsAttention = 0;
  let atRisk = 0;
  let sum = 0;
  for (const c of scored) {
    const band = bandFor(c.healthScore);
    if (band === "excellent") excellent += 1;
    else if (band === "healthy") healthy += 1;
    else if (band === "needs_attention") needsAttention += 1;
    else atRisk += 1;
    sum += c.healthScore;
  }
  return {
    total: clients.length,
    scored: scored.length,
    excellent,
    healthy,
    needsAttention,
    atRisk,
    averageScore: scored.length === 0 ? null : Math.round(sum / scored.length),
  };
}
